import { environment } from '@/../environments/environment';
import { AlertService } from '@/services/alert.service';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ErrorBusinessCode } from '@eemo/api-client';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthenticationService } from '../services/authentication.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private router = inject(Router);
  private authenticationService = inject(AuthenticationService);
  private alertService = inject(AlertService);
  private dialog = inject(MatDialog);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        let silentErr = false;
        if (request.url.match('version.*')) {
          // special case for version check : we don't want any error to be thrown and shown in the alert service
          silentErr = true;
        } else if (request.url.endsWith('/view-error')) {
          // special case for the document view error report : the error is already shown by the document viewer
          // (and the report must not fail when the api does not provide it yet)
          silentErr = true;
        } else if (request.url.includes('/api-adresse/search')) {
          // special case for api address : error are managed locally
          silentErr = false;
        } else if (err.status === 401) {
          this.expireSession();
          if (request.url.match('users/session/idp')) {
            // 401 error on session idp -> big problem !
            this.alertService.apiServerError(err, 'Unauthorized login discover');
          } else {
            // 401 on login page : authentication system error
            this.alertService.apiServerError(err, 'Authentication error');
          }
        } else if (err.status === 403 && request.url.match('properties/*') && request.method === 'GET') {
          // special case for property access deny : we do nothing : it shows a view allowing to ask an access
        } else if (err.status === 403) {
          this.alertService.error("Vous n'êtes pas autorisé à accéder à cette ressource");
          if (!request.url.match('users/session')) {
            //attempt to reload user session to take change into account (role update; subscription expired...)
            this.authenticationService.refreshUserSession();
          } else {
            //403 on session -> invalid credentials
            this.expireSession();
          }
        } else if (err.status === 400 || err.status === 404) {
          if (err.status === 400 && err.error.businessCode === ErrorBusinessCode.RECAPTCHA_VALIDATION_FAILED) {
            //recaptcha failed due to inactivity: reload the page
            this.alertService.success('Inactivité détectée, rechargement...');
            setTimeout(() => {
              location.reload();
            }, 1000);
          }
          if (!err.error.businessCode) {
            this.alertService.apiBadRequest(err);
          }
          // errors with business code are not managed globally but by components
        } else if (err.status === 413) {
          this.alertService.error(
            'Le document est trop volumineux. La limite est de ' + environment.MAX_UPLOAD_SIZE + ' Mo.',
          );
        } else if (err.status === 501) {
          this.alertService.warning('Fonctionnalité non disponible');
        } else if (err.status === 503) {
          setTimeout(() => {
            location.reload();
          }, 3000);
        } else {
          this.alertService.apiServerError(err);
        }
        if (silentErr) {
          return EMPTY;
        }
        return throwError(err);
      }),
    );
  }

  expireSession(): void {
    if (this.authenticationService) {
      this.authenticationService.resetAuthCache();
    }
    // if not on login page -> reload
    if (!this.router.url.startsWith('/login')) {
      this.dialog.closeAll();
      setTimeout(() => this.alertService.warning('Session expirée'));
      // we want only the path in the returnUrl param, not the query params
      const params = { returnUrl: this.router.routerState.snapshot.url.split('?')[0] };
      this.router.navigate(['/login'], {
        queryParams: params,
      });
    }
  }
}
